import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { TrendingUp, TrendingDown, Wallet, Users, Calendar, AlertCircle, Warehouse, Coins, Package, CheckCircle, AlertTriangle, XCircle, MapPin, ShoppingCart, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDeviceType } from '@/hooks/useDeviceType';
import dashboardService, { MonthlyStats, CategoryStats, ArticleCategoryStats, ProvinceStats, UnpaidMovement, DateRangeFilter, PaymentTypeStats, SoldArticleStats, SoldArticlesSummary, CustomerReportResponse } from '../../services/dashboardService';
import CustomerReportSection from './CustomerReportSection';
import articoliService from '../../services/articoliService';
import { DashboardStats, Articolo } from '../../types';
import { getRegione } from '@/utils/province';
import { useTipiContatto } from '@/hooks/useTipiContatto';
import styles from './ReportPage.module.css';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import {
  exportToExcelMultiSheet,
  exportToPdfMultiSection,
  ExportColumn,
  SheetConfig,
  formatPeriod,
} from '@/utils/exports';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type Period = 'today' | 'week' | 'month' | 'year' | 'custom';

/** Orizzonte con cui si filtra lo scadenzario dei movimenti non pagati */
type UnpaidHorizon = 'SCADUTI' | 'ENTRO30' | 'OLTRE30' | 'TUTTI';

/** Righe di scadenzario mostrate prima di espandere l'elenco completo */
const UNPAID_PREVIEW_ROWS = 10;

/** Metrica con cui si legge il grafico delle vendite per provincia */
type ProvinceMetric = 'fatturato' | 'movimenti' | 'media';

const PROVINCE_METRICS: Record<ProvinceMetric, { label: string; axisLabel: string }> = {
  fatturato: { label: 'Fatturato', axisLabel: 'Fatturato' },
  movimenti: { label: 'Movimenti', axisLabel: 'Numero movimenti' },
  media: { label: 'Valore medio', axisLabel: 'Valore medio per movimento' },
};

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper functions to calculate date ranges
const getDateRange = (period: Period, customStart?: string, customEnd?: string): DateRangeFilter => {
  const today = new Date();

  switch (period) {
    case 'today':
      return {
        startDate: formatDateLocal(today),
        endDate: formatDateLocal(today),
      };
    case 'week': {
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
      startOfWeek.setDate(diff);
      return {
        startDate: formatDateLocal(startOfWeek),
        endDate: formatDateLocal(today),
      };
    }
    case 'month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: formatDateLocal(startOfMonth),
        endDate: formatDateLocal(today),
      };
    }
    case 'year': {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return {
        startDate: formatDateLocal(startOfYear),
        endDate: formatDateLocal(today),
      };
    }
    case 'custom':
      if (customStart && customEnd) {
        // Validate: ensure startDate <= endDate, swap if inverted
        if (customStart > customEnd) {
          return {
            startDate: customEnd,
            endDate: customStart,
          };
        }
        return {
          startDate: customStart,
          endDate: customEnd,
        };
      }
      // Default to current month if custom dates not set
      return {
        startDate: formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1)),
        endDate: formatDateLocal(today),
      };
    default:
      return {};
  }
};

export const ReportPage = () => {
  const { isMobile } = useDeviceType();
  const { label: tipoContattoLabel } = useTipiContatto();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('year');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // State for API data
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [articleCategoryStats, setArticleCategoryStats] = useState<ArticleCategoryStats[]>([]);
  const [provinceStats, setProvinceStats] = useState<ProvinceStats[]>([]);
  const [paymentTypeStats, setPaymentTypeStats] = useState<PaymentTypeStats[]>([]);
  const [customerReport, setCustomerReport] = useState<CustomerReportResponse | null>(null);
  const [customerArticoloId, setCustomerArticoloId] = useState<number | null>(null);
  const [customerProvincia, setCustomerProvincia] = useState<string>('');
  const [unpaidMovements, setUnpaidMovements] = useState<UnpaidMovement[]>([]);
  const [unpaidHorizon, setUnpaidHorizon] = useState<UnpaidHorizon>('TUTTI');
  const [unpaidExpanded, setUnpaidExpanded] = useState(false);
  const [provinceMetric, setProvinceMetric] = useState<ProvinceMetric>('fatturato');
  const [provinceLimit, setProvinceLimit] = useState<number>(10);
  const [provinceRegione, setProvinceRegione] = useState<string>('');
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [soldArticlesStats, setSoldArticlesStats] = useState<SoldArticleStats[]>([]);
  const [soldArticlesSummary, setSoldArticlesSummary] = useState<SoldArticlesSummary | null>(null);
  const [soldArticlesLimit, setSoldArticlesLimit] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Load report data - wrapped in useCallback to avoid stale closures
  const loadReportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(period, customStartDate, customEndDate);

      const [statsData, monthly, categories, articleCategories, provinces, paymentTypes, unpaid, articoliData, soldArticles, soldSummary] = await Promise.all([
        dashboardService.getStats(dateRange),
        dashboardService.getMonthlyStats(undefined, dateRange),
        dashboardService.getCategoryStats(dateRange),
        dashboardService.getArticleCategoryStats(dateRange),
        dashboardService.getProvinceStats(dateRange),
        dashboardService.getPaymentTypeStats(dateRange),
        dashboardService.getUnpaidMovements(),
        articoliService.getArticoli(),
        dashboardService.getSoldArticlesStats(soldArticlesLimit, dateRange),
        dashboardService.getSoldArticlesSummary(dateRange),
      ]);

      setStats(statsData);
      setMonthlyStats(monthly);
      setCategoryStats(categories);
      setArticleCategoryStats(articleCategories);
      setProvinceStats(provinces);
      setPaymentTypeStats(paymentTypes);
      setUnpaidMovements(unpaid);
      setArticoli(articoliData);
      setSoldArticlesStats(soldArticles);
      setSoldArticlesSummary(soldSummary);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [period, customStartDate, customEndDate, soldArticlesLimit]);

  // Load report data on mount and when filters change
  useEffect(() => {
    // Don't load if custom period is selected but dates are incomplete
    if (period === 'custom' && (!customStartDate || !customEndDate)) {
      setLoading(false); // Prevent infinite loading spinner
      return;
    }
    loadReportData();
  }, [loadReportData]);

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    if (!stats) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const dateRange = getDateRange(period, customStartDate, customEndDate);
      const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
      const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;
      const periodString = formatPeriod(startDate, endDate);

      // Sheet 1 - Riepilogo
      const riepilogoColumns: ExportColumn[] = [
        { field: 'voce', header: 'Voce', type: 'text', width: 30 },
        { field: 'valore', header: 'Valore', type: 'currency', width: 20 },
        { field: 'percentuale', header: '% su entrate', type: 'text', width: 18 },
      ];
      const riepilogoData = [
        { voce: 'Periodo analizzato', valore: periodString || 'N/A', percentuale: '' },
        { voce: 'Totale Entrate', valore: stats.totaleEntrate, percentuale: stats.totaleEntrate > 0 ? formatPercent(100) : '' },
        { voce: 'Totale Uscite', valore: stats.totaleUscite, percentuale: percentuali.incidenzaUscite !== null ? formatPercent(percentuali.incidenzaUscite) : '' },
        { voce: 'Saldo', valore: stats.saldo, percentuale: percentuali.margine !== null ? `${formatPercent(percentuali.margine)} di margine` : '' },
      ];

      // Sheet 2 - Andamento Mensile
      const monthLabels = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
      const mensileColumns: ExportColumn[] = [
        { field: 'mese', header: 'Mese', type: 'text', width: 15 },
        { field: 'entrate', header: 'Entrate', type: 'currency', width: 15 },
        { field: 'uscite', header: 'Uscite', type: 'currency', width: 15 },
        { field: 'saldo', header: 'Saldo', type: 'currency', width: 15 },
      ];
      const mensileData = monthlyStats.map(stat => {
        let meseLabel = 'N/A';
        if (typeof stat.mese === 'string') {
          const monthNum = parseInt(stat.mese.split('-')[1], 10);
          meseLabel = monthLabels[monthNum - 1] || stat.mese;
        } else {
          meseLabel = monthLabels[stat.mese - 1] || String(stat.mese);
        }
        return {
          mese: meseLabel,
          entrate: stat.entrate,
          uscite: stat.uscite,
          saldo: stat.entrate - stat.uscite,
        };
      });

      // Sheet 3 - Per Categoria
      const categoriaColumns: ExportColumn[] = [
        { field: 'categoria', header: 'Categoria', type: 'text', width: 25 },
        { field: 'tipo', header: 'Tipo', type: 'text', width: 12 },
        { field: 'movimenti', header: 'Movimenti', type: 'number', width: 12 },
        { field: 'totale', header: 'Totale', type: 'currency', width: 15 },
        { field: 'percentuale', header: '%', type: 'percentage', width: 10 },
      ];
      const totalCategoriaValue = categoryStats.reduce((sum, c) => sum + c.totale, 0);
      const categoriaData = categoryStats.map(cat => ({
        categoria: cat.nome || cat.categoriaNome || '',
        tipo: cat.tipo === 'ENTRATA' ? 'Entrata' : 'Uscita',
        movimenti: cat.numeroMovimenti || 0,
        totale: cat.totale,
        percentuale: totalCategoriaValue > 0 ? (cat.totale / totalCategoriaValue) * 100 : 0,
      }));

      // Sheet 4 - Clienti (report dettagliato)
      const clientiColumns: ExportColumn[] = [
        { field: 'posizione', header: '#', type: 'number', width: 5 },
        { field: 'cliente', header: 'Contatto', type: 'text', width: 25 },
        { field: 'codice', header: 'Codice', type: 'text', width: 12 },
        { field: 'tipoContatto', header: 'Tipo', type: 'text', width: 14 },
        { field: 'zona', header: 'Zona', type: 'text', width: 20 },
        { field: 'movimenti', header: 'Movimenti', type: 'number', width: 12 },
        { field: 'quantita', header: 'Quantità', type: 'number', width: 12 },
        { field: 'articoliDistinti', header: 'Articoli diversi', type: 'number', width: 14 },
        { field: 'articoloTop', header: 'Articolo più acquistato', type: 'text', width: 25 },
        { field: 'valoreMedio', header: 'Valore medio', type: 'currency', width: 14 },
        { field: 'ultimoAcquisto', header: 'Ultimo acquisto', type: 'text', width: 14 },
        { field: 'incassato', header: 'Incassato', type: 'currency', width: 14 },
        { field: 'daIncassare', header: 'Da incassare', type: 'currency', width: 14 },
        { field: 'totale', header: 'Totale', type: 'currency', width: 15 },
        { field: 'percentuale', header: '%', type: 'percentage', width: 10 },
      ];
      const clientiReport = customerReport?.clienti || [];
      const clientiData = clientiReport.map((cust, idx) => ({
        posizione: idx + 1,
        cliente: cust.clienteNome,
        codice: cust.codiceProgressivo || '',
        tipoContatto: cust.tipoContatto ? tipoContattoLabel(cust.tipoContatto) : '',
        zona: [cust.citta, cust.provincia ? `(${cust.provincia})` : ''].filter(Boolean).join(' '),
        movimenti: cust.numeroMovimenti,
        quantita: cust.quantitaTotale,
        articoliDistinti: cust.articoliDistinti,
        articoloTop: cust.articoloTopNome || '',
        valoreMedio: cust.scontrinoMedio,
        ultimoAcquisto: cust.ultimoAcquisto ? new Date(cust.ultimoAcquisto).toLocaleDateString('it-IT') : '',
        incassato: cust.totaleIncassato,
        daIncassare: cust.totaleDaIncassare,
        totale: cust.totale,
        percentuale: cust.percentualeSulTotale,
      }));

      // Sheet 6 - Chi ha comprato che cosa (incrocio cliente/articolo)
      const clientiArticoliColumns: ExportColumn[] = [
        { field: 'cliente', header: 'Contatto', type: 'text', width: 25 },
        { field: 'articolo', header: 'Articolo / Voce', type: 'text', width: 25 },
        { field: 'codice', header: 'Codice', type: 'text', width: 12 },
        { field: 'merceologia', header: 'Merceologia', type: 'text', width: 18 },
        { field: 'quantita', header: 'Quantità', type: 'text', width: 12 },
        { field: 'prezzoMedio', header: 'Prezzo medio', type: 'currency', width: 14 },
        { field: 'totale', header: 'Totale', type: 'currency', width: 15 },
        { field: 'movimenti', header: 'Movimenti', type: 'number', width: 12 },
        { field: 'ultimoAcquisto', header: 'Ultimo acquisto', type: 'text', width: 14 },
      ];
      const clientiArticoliData = (customerReport?.dettaglioArticoli || []).map((riga) => ({
        cliente: riga.clienteNome,
        articolo: riga.voceReparto ? `Reparto: ${riga.articoloNome}` : riga.articoloNome,
        codice: riga.articoloCodice || '',
        merceologia: riga.categoriaArticoloNome || '',
        quantita: riga.voceReparto ? '' : `${riga.quantita.toLocaleString('it-IT')} ${riga.unitaMisura}`,
        prezzoMedio: riga.voceReparto ? null : riga.prezzoMedio,
        totale: riga.ricavo,
        movimenti: riga.numeroMovimenti,
        ultimoAcquisto: riga.ultimoAcquisto ? new Date(riga.ultimoAcquisto).toLocaleDateString('it-IT') : '',
      }));

      // Sheet 5 - Articoli Venduti
      const articoliVendutiColumns: ExportColumn[] = [
        { field: 'posizione', header: '#', type: 'number', width: 5 },
        { field: 'articolo', header: 'Articolo', type: 'text', width: 25 },
        { field: 'codice', header: 'Codice', type: 'text', width: 12 },
        { field: 'categoria', header: 'Categoria', type: 'text', width: 15 },
        { field: 'quantita', header: 'Qtà Venduta', type: 'text', width: 12 },
        { field: 'prezzoMedio', header: 'Prezzo Medio', type: 'currency', width: 12 },
        { field: 'ricavo', header: 'Ricavo Totale', type: 'currency', width: 15 },
        { field: 'costoGestione', header: 'Costo Gestione Medio', type: 'currency', width: 15 },
        { field: 'costoTotale', header: 'Costo Totale', type: 'currency', width: 15 },
        { field: 'ricavoNetto', header: 'Ricavo Netto', type: 'currency', width: 15 },
        { field: 'percentuale', header: '%', type: 'percentage', width: 10 },
      ];
      const articoliVendutiData = soldArticlesStats.map((art, idx) => ({
        posizione: idx + 1,
        articolo: art.articoloNome,
        codice: art.articoloCodice || '',
        categoria: art.categoriaArticoloNome || '',
        quantita: `${art.quantitaVenduta.toLocaleString('it-IT')} ${art.unitaMisura}`,
        prezzoMedio: art.prezzoMedioVendita,
        ricavo: art.ricavoTotale,
        costoGestione: art.costoGestioneMedio ?? 0,
        costoTotale: art.costoTotale ?? 0,
        ricavoNetto: art.ricavoNetto ?? 0,
        percentuale: art.percentualeSulTotale,
      }));

      // Sheet - Merceologia
      const merceologiaColumns: ExportColumn[] = [
        { field: 'merceologia', header: 'Merceologia', type: 'text', width: 25 },
        { field: 'movimenti', header: 'Movimenti', type: 'number', width: 12 },
        { field: 'totale', header: 'Totale', type: 'currency', width: 15 },
        { field: 'percentuale', header: '%', type: 'percentage', width: 10 },
      ];
      const merceologiaData = articleCategoryRows.map((cat) => ({
        merceologia: cat.categoriaArticoloNome,
        movimenti: cat.numeroMovimenti || 0,
        totale: cat.totale,
        percentuale: cat.percentualeDelTotale,
      }));

      // Sheet - Vendite per Provincia
      const provinceColumns: ExportColumn[] = [
        { field: 'posizione', header: '#', type: 'number', width: 5 },
        { field: 'provincia', header: 'Provincia', type: 'text', width: 15 },
        { field: 'regione', header: 'Regione', type: 'text', width: 18 },
        { field: 'movimenti', header: 'Movimenti', type: 'number', width: 12 },
        { field: 'valoreMedio', header: 'Valore medio', type: 'currency', width: 14 },
        { field: 'totale', header: 'Fatturato', type: 'currency', width: 15 },
        { field: 'percentuale', header: '%', type: 'percentage', width: 10 },
      ];
      const provinceData = provinceRows.map((prov, idx) => ({
        posizione: idx + 1,
        provincia: prov.provincia,
        regione: prov.regione,
        movimenti: prov.numeroMovimenti,
        valoreMedio: prov.mediaPerMovimento,
        totale: prov.totale,
        percentuale: prov.percentualeDelTotale,
      }));

      // Sheet - Non Pagati (scadenzario completo)
      const nonPagatiColumns: ExportColumn[] = [
        { field: 'scadenza', header: 'Scadenza', type: 'date', width: 14 },
        { field: 'contatto', header: 'Contatto', type: 'text', width: 24 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 30 },
        { field: 'categoria', header: 'Categoria', type: 'text', width: 20 },
        { field: 'tipo', header: 'Tipo', type: 'text', width: 12 },
        { field: 'importo', header: 'Importo', type: 'currency', width: 15 },
        { field: 'stato', header: 'Stato', type: 'text', width: 16 },
      ];
      const nonPagatiData = unpaidMovements.map((mov) => ({
        scadenza: mov.dataScadenza || mov.dataMovimento,
        contatto: mov.clienteNome || '-',
        descrizione: mov.descrizione || '-',
        categoria: mov.categoriaNome || '-',
        tipo: mov.tipo === 'ENTRATA' ? 'Da incassare' : 'Da pagare',
        importo: mov.importo,
        stato: mov.isOverdue ? `Scaduto da ${Math.abs(mov.giorniScadenza)} gg` : getScadenzaLabel(mov.giorniScadenza),
      }));

      const sheets: SheetConfig[] = [
        {
          name: 'Riepilogo',
          title: 'Riepilogo',
          columns: riepilogoColumns,
          rows: riepilogoData,
        },
        {
          name: 'Andamento Mensile',
          title: 'Andamento Mensile',
          columns: mensileColumns,
          rows: mensileData,
        },
        {
          name: 'Per Categoria',
          title: 'Per Categoria',
          columns: categoriaColumns,
          rows: categoriaData,
        },
        {
          name: 'Clienti',
          title: 'Report Clienti',
          columns: clientiColumns,
          rows: clientiData,
        },
      ];

      // Merceologia: totali in euro oltre alle percentuali del grafico
      if (merceologiaData.length > 0) {
        sheets.push({
          name: 'Merceologia',
          title: 'Movimenti per Merceologia',
          columns: merceologiaColumns,
          rows: merceologiaData,
        });
      }

      // Vendite per provincia
      if (provinceData.length > 0) {
        sheets.push({
          name: 'Vendite per Provincia',
          title: provinceRegione
            ? `Vendite per Provincia - ${provinceRegione}`
            : 'Vendite per Provincia',
          columns: provinceColumns,
          rows: provinceData,
        });
      }

      // Scadenzario: tutti i movimenti ancora aperti, scaduto compreso
      if (nonPagatiData.length > 0) {
        sheets.push({
          name: 'Non Pagati',
          title: 'Movimenti Non Pagati',
          columns: nonPagatiColumns,
          rows: nonPagatiData,
        });
      }

      // Add Articoli Venduti sheet if there's data
      if (soldArticlesStats.length > 0) {
        sheets.push({
          name: 'Articoli Venduti',
          title: 'Articoli Venduti',
          columns: articoliVendutiColumns,
          rows: articoliVendutiData,
        });
      }

      // Incrocio cliente/articolo: chi ha comprato che cosa
      if (clientiArticoliData.length > 0) {
        sheets.push({
          name: 'Clienti x Articoli',
          title: 'Chi ha comprato che cosa',
          columns: clientiArticoliColumns,
          rows: clientiArticoliData,
        });
      }

      const config = {
        title: 'Report',
        filename: periodString ? `report_${periodString.replace(/\s+/g, '_')}` : 'report',
        sheets,
      };

      if (format === 'excel') {
        await exportToExcelMultiSheet(config);
      } else {
        await exportToPdfMultiSection(config, periodString ? `Periodo: ${periodString}` : undefined);
      }
    } catch (err) {
      console.error('Error exporting report:', err);
      alert('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const lineChartData = useMemo(() => {
    if (!monthlyStats.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const monthLabels = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

    // Filter out months with no data and parse month from string format "2025-09"
    const statsWithData = monthlyStats.filter((stat) => stat.entrate > 0 || stat.uscite > 0);

    const labels = statsWithData.map((stat) => {
      if (typeof stat.mese === 'string') {
        const monthNum = parseInt(stat.mese.split('-')[1], 10);
        return monthLabels[monthNum - 1];
      }
      return monthLabels[stat.mese - 1];
    });
    const entrateData = statsWithData.map((stat) => stat.entrate);
    const usciteData = statsWithData.map((stat) => stat.uscite);

    return {
      labels,
      datasets: [
        {
          label: 'Entrate',
          data: entrateData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Uscite',
          data: usciteData,
          borderColor: '#F44336',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [monthlyStats]);

  const barChartData = useMemo(() => {
    if (!monthlyStats.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const monthLabels = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

    // Filter out months with no data and parse month from string format "2025-09"
    const statsWithData = monthlyStats.filter((stat) => stat.entrate > 0 || stat.uscite > 0);

    const labels = statsWithData.map((stat) => {
      if (typeof stat.mese === 'string') {
        const monthNum = parseInt(stat.mese.split('-')[1], 10);
        return monthLabels[monthNum - 1];
      }
      return monthLabels[stat.mese - 1];
    });
    const entrateData = statsWithData.map((stat) => stat.entrate);
    const usciteData = statsWithData.map((stat) => stat.uscite);

    return {
      labels,
      datasets: [
        {
          label: 'Entrate',
          data: entrateData,
          backgroundColor: '#4CAF50',
        },
        {
          label: 'Uscite',
          data: usciteData,
          backgroundColor: '#F44336',
        },
      ],
    };
  }, [monthlyStats]);

  // Doughnut data for USCITE
  const doughnutUsciteData = useMemo(() => {
    const usciteStats = categoryStats.filter((cat) => cat.tipo === 'USCITA');
    if (!usciteStats.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const colors = ['#F44336', '#FF5722', '#FF9800', '#FFC107', '#795548', '#9E9E9E'];
    const labels = usciteStats.map((cat) => cat.categoriaNome || cat.nome);
    const data = usciteStats.map((cat) => cat.totale);
    const backgroundColor = usciteStats.map((cat, index) => cat.colore || colors[index % colors.length]);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  }, [categoryStats]);

  // Doughnut data for ENTRATE
  const doughnutEntrateData = useMemo(() => {
    const entrateStats = categoryStats.filter((cat) => cat.tipo === 'ENTRATA');
    if (!entrateStats.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const colors = ['#4CAF50', '#8BC34A', '#CDDC39', '#00BCD4', '#2196F3', '#3F51B5'];
    const labels = entrateStats.map((cat) => cat.categoriaNome || cat.nome);
    const data = entrateStats.map((cat) => cat.totale);
    const backgroundColor = entrateStats.map((cat, index) => cat.colore || colors[index % colors.length]);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  }, [categoryStats]);

  // Merceologia: righe con colore risolto, condivise da grafico e tabella
  const articleCategoryRows = useMemo(() => {
    const colors = ['#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A'];
    return articleCategoryStats.map((cat, index) => ({
      ...cat,
      colore: cat.colore || colors[index % colors.length],
    }));
  }, [articleCategoryStats]);

  // Totali di riga per il piede della tabella merceologia
  const articleCategoryTotals = useMemo(
    () => ({
      totale: articleCategoryRows.reduce((sum, cat) => sum + (cat.totale || 0), 0),
      numeroMovimenti: articleCategoryRows.reduce((sum, cat) => sum + (cat.numeroMovimenti || 0), 0),
    }),
    [articleCategoryRows]
  );

  // Doughnut data for ARTICLE CATEGORIES
  const doughnutArticleCategoryData = useMemo(() => {
    if (!articleCategoryRows.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: articleCategoryRows.map((cat) => cat.categoriaArticoloNome),
      datasets: [
        {
          data: articleCategoryRows.map((cat) => cat.totale),
          backgroundColor: articleCategoryRows.map((cat) => cat.colore),
          borderWidth: 2,
          borderColor: '#fff',
        },
      ],
    };
  }, [articleCategoryRows]);

  // Vendite per provincia: righe con valore medio per movimento (ordinate per fatturato dal backend)
  const provinceRowsAll = useMemo(
    () =>
      provinceStats.map((p) => ({
        ...p,
        mediaPerMovimento: p.numeroMovimenti > 0 ? p.totale / p.numeroMovimenti : 0,
        regione: getRegione(p.provincia),
      })),
    [provinceStats]
  );

  // Regioni presenti nei dati, dalla piu venduta alla meno venduta
  const provinceRegioni = useMemo(() => {
    const perRegione = new Map<string, { regione: string; numeroProvince: number; totale: number }>();
    provinceRowsAll.forEach((p) => {
      const voce = perRegione.get(p.regione) || { regione: p.regione, numeroProvince: 0, totale: 0 };
      voce.numeroProvince += 1;
      voce.totale += p.totale || 0;
      perRegione.set(p.regione, voce);
    });
    return [...perRegione.values()].sort((a, b) => b.totale - a.totale);
  }, [provinceRowsAll]);

  // Se la regione scelta sparisce dopo un cambio di periodo si torna a "tutte"
  useEffect(() => {
    if (provinceRegione && !provinceRegioni.some((r) => r.regione === provinceRegione)) {
      setProvinceRegione('');
    }
  }, [provinceRegioni, provinceRegione]);

  // Righe visibili: la quota e sempre calcolata sul totale mostrato, cosi
  // filtrando per regione le percentuali restano leggibili e sommano a 100
  const provinceRows = useMemo(() => {
    const filtrate = provinceRegione
      ? provinceRowsAll.filter((p) => p.regione === provinceRegione)
      : provinceRowsAll;
    const totale = filtrate.reduce((sum, p) => sum + (p.totale || 0), 0);
    return filtrate.map((p) => ({
      ...p,
      percentualeDelTotale: totale > 0 ? (p.totale / totale) * 100 : 0,
    }));
  }, [provinceRowsAll, provinceRegione]);

  // Riepilogo territoriale mostrato nelle card sopra la tabella
  const provinceSummary = useMemo(() => {
    if (!provinceRows.length) {
      return null;
    }
    const totale = provinceRows.reduce((sum, p) => sum + (p.totale || 0), 0);
    const movimenti = provinceRows.reduce((sum, p) => sum + (p.numeroMovimenti || 0), 0);
    return {
      numeroProvince: provinceRows.length,
      movimenti,
      totale,
      mediaPerMovimento: movimenti > 0 ? totale / movimenti : 0,
      topProvincia: provinceRows[0],
    };
  }, [provinceRows]);

  // Valore della provincia secondo la metrica scelta nel grafico
  const provinceMetricValue = useCallback(
    (row: (typeof provinceRows)[number], metric: ProvinceMetric) => {
      if (metric === 'movimenti') return row.numeroMovimenti;
      if (metric === 'media') return row.mediaPerMovimento;
      return row.totale;
    },
    []
  );

  // Righe del grafico: riordinate sulla metrica scelta e limitate alle prime N
  const provinceChartRows = useMemo(() => {
    const sorted = [...provinceRows].sort(
      (a, b) => provinceMetricValue(b, provinceMetric) - provinceMetricValue(a, provinceMetric)
    );
    return sorted.slice(0, provinceLimit);
  }, [provinceRows, provinceMetric, provinceLimit, provinceMetricValue]);

  // Barre dal blu al verde acqua: la sfumatura segue il ranking, non il numero di barre
  const provinceBarColor = (index: number, count: number) => {
    const t = count <= 1 ? 0 : index / (count - 1);
    return `hsl(${222 - t * 48}, ${72 - t * 14}%, ${48 + t * 11}%)`;
  };

  // Bar data for PROVINCE
  const provinceBarData = useMemo(() => {
    if (provinceChartRows.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: provinceChartRows.map((p) => p.provincia),
      datasets: [
        {
          label: PROVINCE_METRICS[provinceMetric].label,
          data: provinceChartRows.map((p) => provinceMetricValue(p, provinceMetric)),
          backgroundColor: provinceChartRows.map((_, index) =>
            provinceBarColor(index, provinceChartRows.length)
          ),
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 28,
        },
      ],
    };
  }, [provinceChartRows, provinceMetric, provinceMetricValue]);

  // Bar data for sold articles (horizontal)
  const soldArticlesBarData = useMemo(() => {
    const top10 = soldArticlesStats.slice(0, 10);

    if (top10.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: top10.map(art => art.articoloNome),
      datasets: [{
        label: 'Ricavo Totale',
        data: top10.map(art => art.ricavoTotale),
        backgroundColor: top10.map((_, index) => {
          // Gradiente verde -> azzurro
          const hue = 120 - (index * 12); // da 120 (verde) a 0
          return `hsl(${hue}, 60%, 50%)`;
        }),
      }],
    };
  }, [soldArticlesStats]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? ('bottom' as const) : ('right' as const),
        labels: {
          padding: 15,
          usePointStyle: true,
          generateLabels: (chart: any) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const dataset = data.datasets[0];
              const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
              return data.labels.map((label: string, i: number) => {
                const value = dataset.data[i];
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.backgroundColor[i],
                  lineWidth: 0,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((acc: number, val: number) => acc + val, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            const formattedValue = new Intl.NumberFormat('it-IT', {
              style: 'currency',
              currency: 'EUR',
            }).format(value);
            return `${context.label}: ${formattedValue} (${percentage}%)`;
          },
        },
      },
    },
    cutout: '60%',
  };

  const barChartOptionsHorizontal = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const index = context.dataIndex;
            if (index >= soldArticlesStats.length) return '';
            const art = soldArticlesStats[index];
            return [
              `Ricavo: ${formatCurrency(art.ricavoTotale)}`,
              `Quantità: ${art.quantitaVenduta.toLocaleString('it-IT')} ${art.unitaMisura}`,
              `Prezzo medio: ${formatCurrency(art.prezzoMedioVendita)}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value: string | number) => formatCurrency(typeof value === 'number' ? value : parseFloat(value)),
        },
      },
    },
  };

  // La tabella affiancata fa già da legenda: qui basta la ciambella con i tooltip
  const doughnutOptionsNoLegend = {
    ...doughnutOptions,
    plugins: {
      ...doughnutOptions.plugins,
      legend: { display: false },
    },
  };

  // Etichette valore: sugli assi in forma sintetica, sulle barre per esteso.
  // La notazione compatta si usa solo dai milioni in su ("1,2 Mln"), perche sotto
  // il milione l'inglese "1K" prenderebbe il posto delle migliaia italiane.
  const formatProvinceMetric = (value: number, compact = false) => {
    if (provinceMetric === 'movimenti') {
      return value.toLocaleString('it-IT');
    }
    if (compact && Math.abs(value) >= 1_000_000) {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: compact ? 0 : 2,
      maximumFractionDigits: compact ? 0 : 2,
    }).format(value);
  };

  // react-chartjs-2 registra i plugin solo alla creazione del grafico: il plugin
  // legge il formatter da un ref, cosi segue la metrica scelta senza ricreare il chart
  const provinceLabelFormatRef = useRef<(value: number) => string>(() => '');
  provinceLabelFormatRef.current = (value: number) => formatProvinceMetric(value, isMobile);

  // Chart.js non ha datalabels: il valore viene scritto in fondo a ogni barra,
  // dentro se c'e spazio, altrimenti subito fuori
  const provinceValueLabelPlugin = useMemo(
    () => ({
      id: 'provinceValueLabels',
      afterDatasetsDraw: (chart: any) => {
        const meta = chart.getDatasetMeta(0);
        if (!meta || meta.hidden) return;
        const { ctx, chartArea } = chart;
        ctx.save();
        ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.textBaseline = 'middle';
        meta.data.forEach((bar: any, index: number) => {
          const value = chart.data.datasets[0]?.data[index];
          if (typeof value !== 'number') return;
          const text = provinceLabelFormatRef.current(value);
          const textWidth = ctx.measureText(text).width;
          const fitsInside = bar.x - bar.base > textWidth + 20;
          ctx.textAlign = fitsInside ? 'right' : 'left';
          ctx.fillStyle = fitsInside ? 'rgba(255, 255, 255, 0.96)' : '#475569';
          const x = fitsInside ? bar.x - 10 : Math.min(bar.x + 8, chartArea.right - textWidth);
          ctx.fillText(text, x, bar.y);
        });
        ctx.restore();
      },
    }),
    []
  );

  // Altezza proporzionale al numero di barre: niente barre schiacciate ne spazi vuoti
  const provinceChartHeight = Math.max(
    220,
    provinceChartRows.length * (isMobile ? 34 : 40) + 48
  );

  const provinceBarOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: isMobile ? 10 : 56, top: 4 } },
    onClick: (_event: any, elements: any[]) => {
      const row = elements?.length ? provinceChartRows[elements[0].index] : null;
      if (row) showCustomersForProvince(row.provincia);
    },
    onHover: (event: any, elements: any[]) => {
      const target = event?.native?.target as HTMLElement | undefined;
      if (target) target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        padding: 10,
        callbacks: {
          title: (items: any[]) => {
            const prov = provinceChartRows[items[0]?.dataIndex];
            if (!prov) return '';
            return `Provincia di ${prov.provincia} · ${prov.regione}`;
          },
          label: (context: any) => {
            const prov = provinceChartRows[context.dataIndex];
            if (!prov) return '';
            return [
              `Fatturato: ${formatCurrency(prov.totale)}`,
              `Movimenti: ${prov.numeroMovimenti.toLocaleString('it-IT')}`,
              `Valore medio: ${formatCurrency(prov.mediaPerMovimento)}`,
              `Quota: ${prov.percentualeDelTotale.toFixed(1)}%`,
            ];
          },
          footer: () => 'Clicca la barra per i clienti della provincia',
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: 'rgba(148, 163, 184, 0.22)', drawTicks: false },
        ticks: {
          padding: 6,
          maxTicksLimit: isMobile ? 4 : 6,
          callback: (value: string | number) =>
            formatProvinceMetric(typeof value === 'number' ? value : parseFloat(value), true),
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          padding: 8,
          font: { size: 13, weight: 600 },
        },
      },
    },
  };

  // Scadenzario dei non pagati: un secchiello per orizzonte, piu il totale complessivo
  const unpaidBuckets = useMemo(() => {
    const empty = () => ({ count: 0, daIncassare: 0, daPagare: 0 });
    const buckets: Record<UnpaidHorizon, ReturnType<typeof empty>> = {
      SCADUTI: empty(),
      ENTRO30: empty(),
      OLTRE30: empty(),
      TUTTI: empty(),
    };

    unpaidMovements.forEach((mov) => {
      const horizon: UnpaidHorizon =
        mov.giorniScadenza < 0 ? 'SCADUTI' : mov.giorniScadenza <= 30 ? 'ENTRO30' : 'OLTRE30';
      [horizon, 'TUTTI' as UnpaidHorizon].forEach((key) => {
        const bucket = buckets[key];
        bucket.count += 1;
        if (mov.tipo === 'ENTRATA') {
          bucket.daIncassare += mov.importo;
        } else {
          bucket.daPagare += mov.importo;
        }
      });
    });

    return buckets;
  }, [unpaidMovements]);

  // Righe visibili in tabella: filtrate per orizzonte e, se non espanse, troncate all'anteprima
  const filteredUnpaid = useMemo(() => {
    if (unpaidHorizon === 'TUTTI') return unpaidMovements;
    return unpaidMovements.filter((mov) => {
      if (unpaidHorizon === 'SCADUTI') return mov.giorniScadenza < 0;
      if (unpaidHorizon === 'ENTRO30') return mov.giorniScadenza >= 0 && mov.giorniScadenza <= 30;
      return mov.giorniScadenza > 30;
    });
  }, [unpaidMovements, unpaidHorizon]);

  const visibleUnpaid = unpaidExpanded ? filteredUnpaid : filteredUnpaid.slice(0, UNPAID_PREVIEW_ROWS);

  const changeUnpaidHorizon = (horizon: UnpaidHorizon) => {
    setUnpaidHorizon(horizon);
    setUnpaidExpanded(false);
  };

  // Calculate warehouse stats
  const warehouseStats = useMemo(() => ({
    totale: articoli.length,
    disponibili: articoli.filter(a => a.stato === 'disponibile').length,
    scorteBasse: articoli.filter(a => a.stato === 'scorte_basse').length,
    esauriti: articoli.filter(a => a.stato === 'esaurito').length,
    giacenza: articoli.reduce((sum, a) => sum + (a.quantita || 0), 0),
    valore: articoli.reduce((sum, a) => sum + ((a.prezzoAcquisto || 0) * (a.quantita || 0)), 0),
  }), [articoli]);

  // Percentuali calcolate sul totale delle entrate (incidenza costi e margine di profitto)
  const percentuali = useMemo(() => {
    const entrate = stats?.totaleEntrate || 0;
    const uscite = stats?.totaleUscite || 0;
    const saldo = stats?.saldo ?? entrate - uscite;
    if (entrate <= 0) {
      return { incidenzaUscite: null as number | null, margine: null as number | null };
    }
    return {
      incidenzaUscite: (uscite / entrate) * 100,
      margine: (saldo / entrate) * 100,
    };
  }, [stats]);

  const formatPercent = (value: number) => {
    const formatted = new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
    return `${formatted}%`;
  };

  // Dal report articoli venduti al report clienti: mostra chi ha acquistato l'articolo
  const showCustomersForArticle = (articoloId: number) => {
    setCustomerArticoloId(articoloId);
    setTimeout(() => {
      document.getElementById('report-clienti')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Dal report vendite per provincia al report clienti: mostra chi compra in quella zona
  const showCustomersForProvince = (provincia: string) => {
    setCustomerProvincia(provincia);
    setTimeout(() => {
      document.getElementById('report-clienti')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Data completa: nello scadenzario servono anche mese e anno, non solo giorno/mese
  const formatDateFull = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getScadenzaLabel = (giorni: number) => {
    if (giorni === -1) return 'Ieri';
    if (giorni === 0) return 'Oggi';
    if (giorni === 1) return 'Domani';
    if (giorni < 0) return `${Math.abs(giorni)} gg fa`;
    return `Tra ${giorni} gg`;
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Report</h1>
        <div className={styles.actions}>
          <div className={styles.dateFilters}>
            <select
              className={styles.periodSelect}
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
            >
              <option value="today">Oggi</option>
              <option value="week">Questa settimana</option>
              <option value="month">Questo mese</option>
              <option value="year">Questo anno</option>
              <option value="custom">Personalizzato</option>
            </select>

            {period === 'custom' && (
              <div className={styles.customDateRange}>
                <div className={styles.dateInputWrapper}>
                  <Calendar size={16} />
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    placeholder="Data inizio"
                  />
                </div>
                <span className={styles.dateSeparator}>-</span>
                <div className={styles.dateInputWrapper}>
                  <Calendar size={16} />
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    placeholder="Data fine"
                  />
                </div>
              </div>
            )}
          </div>
          <ExportDropdown
            onExport={handleExport}
            disabled={!stats}
            loading={exportLoading}
          />
        </div>
      </header>

      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Caricamento dati in corso...
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f44336' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Summary Cards */}
          <div className={styles.summaryGrid}>
            <div className={`${styles.summaryCard} ${styles.entrate}`}>
              <div className={styles.summaryIcon}>
                <TrendingUp size={24} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Totale Entrate</span>
                <span className={styles.summaryValue}>
                  {formatCurrency(stats?.totaleEntrate || 0)}
                </span>
              </div>
            </div>

            <div className={`${styles.summaryCard} ${styles.uscite}`}>
              <div className={styles.summaryIcon}>
                <TrendingDown size={24} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Totale Uscite</span>
                <span className={styles.summaryValue}>
                  {formatCurrency(stats?.totaleUscite || 0)}
                </span>
                {percentuali.incidenzaUscite !== null && (
                  <span className={styles.summaryPercent}>
                    {formatPercent(percentuali.incidenzaUscite)} delle entrate
                  </span>
                )}
              </div>
            </div>

            <div className={`${styles.summaryCard} ${styles.saldo}`}>
              <div className={styles.summaryIcon}>
                <Wallet size={24} />
              </div>
              <div className={styles.summaryContent}>
                <span className={styles.summaryLabel}>Saldo</span>
                <span className={styles.summaryValue}>
                  {formatCurrency(stats?.saldo || 0)}
                </span>
                {percentuali.margine !== null && (
                  <span
                    className={`${styles.summaryPercent} ${
                      percentuali.margine >= 0 ? styles.percentPositive : styles.percentNegative
                    }`}
                  >
                    {formatPercent(percentuali.margine)} di margine
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Warehouse Stats Section */}
          {articoli.length > 0 && (
            <div className={styles.warehouseSection}>
              <h3 className={styles.warehouseTitle}>
                <Warehouse size={20} />
                Magazzino
              </h3>
              <div className={styles.warehouseGrid}>
                <div className={styles.warehouseStat}>
                  <Package size={18} style={{ color: 'var(--primary-main)' }} />
                  <span className={styles.warehouseValue}>{warehouseStats.totale}</span>
                  <span className={styles.warehouseLabel}>Articoli</span>
                </div>
                <div className={styles.warehouseStat}>
                  <CheckCircle size={18} style={{ color: 'var(--success-main)' }} />
                  <span className={styles.warehouseValue}>{warehouseStats.disponibili}</span>
                  <span className={styles.warehouseLabel}>Disponibili</span>
                </div>
                <div className={styles.warehouseStat}>
                  <AlertTriangle size={18} style={{ color: 'var(--warning-main)' }} />
                  <span className={styles.warehouseValue}>{warehouseStats.scorteBasse}</span>
                  <span className={styles.warehouseLabel}>Scorte basse</span>
                </div>
                <div className={styles.warehouseStat}>
                  <XCircle size={18} style={{ color: 'var(--error-main)' }} />
                  <span className={styles.warehouseValue}>{warehouseStats.esauriti}</span>
                  <span className={styles.warehouseLabel}>Esauriti</span>
                </div>
                <div className={styles.warehouseStat}>
                  <Warehouse size={18} style={{ color: 'var(--info-main, #2196f3)' }} />
                  <span className={styles.warehouseValue}>{warehouseStats.giacenza.toLocaleString('it-IT')}</span>
                  <span className={styles.warehouseLabel}>Giacenza totale</span>
                </div>
                <div className={styles.warehouseStat}>
                  <Coins size={18} style={{ color: 'var(--success-dark, #1b5e20)' }} />
                  <span className={styles.warehouseValue}>{formatCurrency(warehouseStats.valore)}</span>
                  <span className={styles.warehouseLabel}>Valore magazzino</span>
                </div>
              </div>
            </div>
          )}

          {/* Unpaid Movements Section: scadenzario completo, scaduto in testa */}
          {unpaidMovements.length > 0 && (
            <div className={styles.unpaidSection}>
              <div className={styles.unpaidHeader}>
                <h3 className={styles.unpaidTitle}>
                  <AlertCircle size={20} />
                  Movimenti Non Pagati ({unpaidMovements.length})
                </h3>
                <button
                  className={styles.viewAllLink}
                  onClick={() => navigate('/movimenti?isPagato=false')}
                >
                  Apri in Movimenti
                </button>
              </div>

              {/* Orizzonti temporali: scaduto, prossimi 30 giorni, oltre, tutto */}
              <div className={styles.horizonTabs} role="tablist" aria-label="Orizzonte scadenze">
                {([
                  { key: 'SCADUTI' as UnpaidHorizon, label: 'Scaduti', tone: styles.horizonOverdue },
                  { key: 'ENTRO30' as UnpaidHorizon, label: 'Entro 30 gg', tone: styles.horizonWarning },
                  { key: 'OLTRE30' as UnpaidHorizon, label: 'Oltre 30 gg', tone: styles.horizonNormal },
                  { key: 'TUTTI' as UnpaidHorizon, label: 'Tutti', tone: styles.horizonAll },
                ]).map(({ key, label, tone }) => {
                  const bucket = unpaidBuckets[key];
                  const totale = bucket.daIncassare + bucket.daPagare;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={unpaidHorizon === key}
                      disabled={bucket.count === 0 && key !== 'TUTTI'}
                      className={`${styles.horizonTab} ${tone} ${
                        unpaidHorizon === key ? styles.horizonTabActive : ''
                      }`}
                      onClick={() => changeUnpaidHorizon(key)}
                    >
                      <span className={styles.horizonLabel}>{label}</span>
                      <span className={styles.horizonValue}>{formatCurrency(totale)}</span>
                      <span className={styles.horizonCount}>
                        {bucket.count} {bucket.count === 1 ? 'movimento' : 'movimenti'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Split incassi/pagamenti sull'orizzonte selezionato */}
              <div className={styles.unpaidSplit}>
                <span className={styles.unpaidSplitItem}>
                  <ArrowUpCircle size={16} style={{ color: 'var(--success-main)' }} />
                  Da incassare
                  <strong style={{ color: 'var(--success-main)' }}>
                    {formatCurrency(unpaidBuckets[unpaidHorizon].daIncassare)}
                  </strong>
                </span>
                <span className={styles.unpaidSplitItem}>
                  <ArrowDownCircle size={16} style={{ color: 'var(--error-main)' }} />
                  Da pagare
                  <strong style={{ color: 'var(--error-main)' }}>
                    {formatCurrency(unpaidBuckets[unpaidHorizon].daPagare)}
                  </strong>
                </span>
              </div>

              {filteredUnpaid.length === 0 ? (
                <p className={styles.unpaidEmpty}>Nessun movimento in questo intervallo.</p>
              ) : (
                <>
                  <div className={styles.unpaidTableWrapper}>
                    <table className={styles.unpaidTable}>
                      <thead>
                        <tr>
                          <th>Scadenza</th>
                          <th>Contatto</th>
                          <th className={styles.hideOnMobile}>Descrizione</th>
                          <th className={styles.hideOnMobile}>Categoria</th>
                          <th style={{ textAlign: 'right' }}>Importo</th>
                          <th style={{ textAlign: 'center' }}>Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleUnpaid.map((mov) => (
                          <tr key={mov.id} className={mov.isOverdue ? styles.unpaidRowOverdue : ''}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {formatDateFull(mov.dataScadenza || mov.dataMovimento)}
                            </td>
                            <td>{mov.clienteNome || '-'}</td>
                            <td className={styles.hideOnMobile}>{mov.descrizione || '-'}</td>
                            <td className={styles.hideOnMobile}>{mov.categoriaNome || '-'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 500 }}>
                              <span style={{ color: mov.tipo === 'ENTRATA' ? 'var(--success-main)' : 'var(--error-main)' }}>
                                {mov.tipo === 'ENTRATA' ? '+' : '-'}{formatCurrency(mov.importo)}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`${styles.scadenzaBadge} ${mov.isOverdue ? styles.scadenzaOverdue : mov.giorniScadenza <= 7 ? styles.scadenzaWarning : mov.giorniScadenza <= 30 ? styles.scadenzaSoon : styles.scadenzaNormal}`}>
                                {getScadenzaLabel(mov.giorniScadenza)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredUnpaid.length > UNPAID_PREVIEW_ROWS && (
                    <button
                      type="button"
                      className={styles.unpaidToggle}
                      onClick={() => setUnpaidExpanded((expanded) => !expanded)}
                    >
                      {unpaidExpanded
                        ? 'Mostra solo i primi 10'
                        : `Mostra tutti i ${filteredUnpaid.length} movimenti`}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Sold Articles Report Section */}
          {soldArticlesSummary && soldArticlesStats.length > 0 && (
            <div className={styles.soldArticlesSection}>
              {/* Titolo e controlli */}
              <div className={styles.soldArticlesHeader}>
                <h3 className={styles.soldArticlesTitle}>
                  <Package size={20} />
                  Report Articoli Venduti
                </h3>
                <select
                  className={styles.limitSelect}
                  value={soldArticlesLimit}
                  onChange={(e) => setSoldArticlesLimit(parseInt(e.target.value))}
                >
                  <option value={10}>Top 10</option>
                  <option value={20}>Top 20</option>
                  <option value={50}>Top 50</option>
                  <option value={999999}>Tutti</option>
                </select>
              </div>

              {/* Cards riepilogative */}
              <div className={styles.soldArticlesSummaryGrid}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <Package size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Articoli Venduti</span>
                    <span className={styles.summaryValue}>
                      {soldArticlesSummary.articoliDistinti}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <TrendingUp size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Quantità Totale</span>
                    <span className={styles.summaryValue}>
                      {soldArticlesSummary.quantitaTotale.toLocaleString('it-IT')}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <Coins size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Ricavo Totale</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(soldArticlesSummary.ricavoTotale)}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <Wallet size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Ricavo Medio/Unità</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(soldArticlesSummary.ricavoMedioPerArticolo)}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard} title="Costo di acquisto + costo di gestione medio della categoria, per le quantità vendute">
                  <div className={styles.summaryIcon}>
                    <Coins size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Costo Totale</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(soldArticlesSummary.costoTotale ?? 0)}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard} title="Ricavo totale meno costi di acquisto e di gestione">
                  <div className={styles.summaryIcon}>
                    <TrendingUp size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Ricavo Netto</span>
                    <span
                      className={styles.summaryValue}
                      style={{ color: (soldArticlesSummary.ricavoNettoTotale ?? 0) >= 0 ? 'var(--success-main)' : 'var(--error-main)' }}
                    >
                      {formatCurrency(soldArticlesSummary.ricavoNettoTotale ?? 0)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tabella dettaglio */}
              <div className={styles.soldArticlesTableWrapper}>
                <table className={styles.soldArticlesTable}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>Articolo</th>
                      <th className={styles.hideOnMobile}>Categoria</th>
                      <th style={{ textAlign: 'right' }}>Qtà Venduta</th>
                      <th style={{ textAlign: 'right' }} className={styles.hideOnMobile}>Prezzo Medio</th>
                      <th style={{ textAlign: 'right' }}>Ricavo Totale</th>
                      <th style={{ textAlign: 'right' }} className={styles.hideOnMobile} title="Costo di gestione medio della categoria merceologica">Costo Gestione</th>
                      <th style={{ textAlign: 'right' }} className={styles.hideOnMobile} title="Costo di acquisto + costo di gestione, per le quantità vendute">Costo Totale</th>
                      <th style={{ textAlign: 'right' }} title="Ricavo meno costi di acquisto e di gestione">Netto</th>
                      <th style={{ textAlign: 'right' }}>%</th>
                      <th style={{ width: '110px' }} aria-label="Clienti" />
                    </tr>
                  </thead>
                  <tbody>
                    {soldArticlesStats.map((art, index) => (
                      <tr
                        key={art.articoloId}
                        className={index < 3 ? styles.topRanked : ''}
                      >
                        <td style={{ fontWeight: 600, textAlign: 'center' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && (index + 1)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Package size={14} style={{ color: 'var(--primary-main)', flexShrink: 0 }} />
                            <span>{art.articoloNome}</span>
                          </div>
                        </td>
                        <td className={styles.hideOnMobile}>{art.categoriaArticoloNome || '-'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {art.quantitaVenduta.toLocaleString('it-IT')} {art.unitaMisura}
                        </td>
                        <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                          {formatCurrency(art.prezzoMedioVendita)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--success-main)' }}>
                          {formatCurrency(art.ricavoTotale)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }} className={styles.hideOnMobile}>
                          {art.costoGestioneMedio ? formatCurrency(art.costoGestioneMedio) : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }} className={styles.hideOnMobile}>
                          {formatCurrency(art.costoTotale ?? 0)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: (art.ricavoNetto ?? 0) >= 0 ? 'var(--success-main)' : 'var(--error-main)' }}>
                          {formatCurrency(art.ricavoNetto ?? 0)}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                          {art.percentualeSulTotale.toFixed(1)}%
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => showCustomersForArticle(art.articoloId)}
                            title={`Mostra i clienti che hanno acquistato ${art.articoloNome}`}
                          >
                            <Users size={12} />
                            Clienti
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Grafico a barre (top 10) */}
              {soldArticlesStats.length > 0 && (
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Top 10 Articoli per Ricavo</h3>
                  <div className={styles.chartContainer}>
                    <Bar data={soldArticlesBarData} options={barChartOptionsHorizontal} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sales by Province Report Section */}
          {provinceSummary && (
            <div className={styles.soldArticlesSection}>
              <div className={styles.soldArticlesHeader}>
                <div className={styles.sectionHeading}>
                  <h3 className={styles.soldArticlesTitle}>
                    <MapPin size={20} />
                    Vendite per Provincia
                  </h3>
                  <span className={styles.sectionHint}>
                    Solo entrate incassate con provincia valorizzata
                    {provinceRegione ? ` · filtro regione: ${provinceRegione}` : ''}
                  </span>
                </div>
                {provinceRegioni.length > 1 && (
                  <div className={styles.regionFilter}>
                    <label htmlFor="filtro-regione" className={styles.regionFilterLabel}>
                      Regione
                    </label>
                    <select
                      id="filtro-regione"
                      className={styles.limitSelect}
                      value={provinceRegione}
                      onChange={(e) => setProvinceRegione(e.target.value)}
                    >
                      <option value="">Tutte le regioni ({provinceRowsAll.length} prov.)</option>
                      {provinceRegioni.map((r) => (
                        <option key={r.regione} value={r.regione}>
                          {r.regione} ({r.numeroProvince})
                        </option>
                      ))}
                    </select>
                    {provinceRegione && (
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => setProvinceRegione('')}
                        title="Rimuovi il filtro regione"
                      >
                        <XCircle size={12} />
                        <span className={styles.btnLabel}>Azzera</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Cards riepilogative */}
              <div className={styles.soldArticlesSummaryGrid}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <MapPin size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Province servite</span>
                    <span className={styles.summaryValue}>{provinceSummary.numeroProvince}</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <ShoppingCart size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Movimenti</span>
                    <span className={styles.summaryValue}>
                      {provinceSummary.movimenti.toLocaleString('it-IT')}
                    </span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <Coins size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Fatturato Totale</span>
                    <span className={styles.summaryValue}>{formatCurrency(provinceSummary.totale)}</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryIcon}>
                    <Wallet size={20} />
                  </div>
                  <div className={styles.summaryContent}>
                    <span className={styles.summaryLabel}>Valore Medio</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(provinceSummary.mediaPerMovimento)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tabella dettaglio */}
              <div className={styles.soldArticlesTableWrapper}>
                <table className={styles.soldArticlesTable}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>Provincia</th>
                      <th style={{ textAlign: 'right' }}>Mov.</th>
                      <th style={{ textAlign: 'right' }} className={styles.hideOnMobile}>Valore Medio</th>
                      <th style={{ textAlign: 'right' }}>Fatturato</th>
                      <th style={{ width: '140px' }}>Quota</th>
                      <th style={{ width: '110px' }} aria-label="Clienti" />
                    </tr>
                  </thead>
                  <tbody>
                    {provinceRows.map((prov, index) => (
                      <tr key={prov.provincia} className={index < 3 ? styles.topRanked : ''}>
                        <td style={{ fontWeight: 600, textAlign: 'center' }}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && index + 1}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MapPin size={14} style={{ color: 'var(--primary-main)', flexShrink: 0 }} />
                            <span>{prov.provincia}</span>
                            {!provinceRegione && (
                              <span className={`${styles.regionTag} ${styles.hideOnMobile}`}>
                                {prov.regione}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>{prov.numeroMovimenti.toLocaleString('it-IT')}</td>
                        <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                          {formatCurrency(prov.mediaPerMovimento)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--success-main)' }}>
                          {formatCurrency(prov.totale)}
                        </td>
                        <td>
                          <div className={styles.shareCell}>
                            <div className={styles.shareBar}>
                              <div
                                className={styles.shareBarFill}
                                style={{ width: `${Math.min(prov.percentualeDelTotale, 100)}%` }}
                              />
                            </div>
                            <span className={styles.shareValue}>{prov.percentualeDelTotale.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => showCustomersForProvince(prov.provincia)}
                            title={`Mostra i clienti della provincia di ${prov.provincia}`}
                          >
                            <Users size={12} />
                            <span className={styles.btnLabel}>Clienti</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td />
                      <td style={{ fontWeight: 600 }}>Totale</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {provinceSummary.movimenti.toLocaleString('it-IT')}
                      </td>
                      <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                        {formatCurrency(provinceSummary.mediaPerMovimento)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatCurrency(provinceSummary.totale)}
                      </td>
                      <td style={{ fontWeight: 600 }}>100%</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Grafico a barre: metrica e profondita selezionabili, click sulla barra per i clienti */}
              {provinceBarData.labels.length > 0 && (
                <div className={styles.chartCard}>
                  <div className={styles.provinceChartHeader}>
                    <h3 className={styles.chartTitle} style={{ margin: 0 }}>
                      Province per {PROVINCE_METRICS[provinceMetric].label.toLowerCase()}
                      {provinceRegione ? ` · ${provinceRegione}` : ''}
                    </h3>
                    <div className={styles.provinceChartControls}>
                      <div className={styles.metricSwitch} role="group" aria-label="Metrica del grafico">
                        {(Object.keys(PROVINCE_METRICS) as ProvinceMetric[]).map((metric) => (
                          <button
                            key={metric}
                            type="button"
                            aria-pressed={provinceMetric === metric}
                            className={`${styles.metricButton} ${
                              provinceMetric === metric ? styles.metricButtonActive : ''
                            }`}
                            onClick={() => setProvinceMetric(metric)}
                          >
                            {PROVINCE_METRICS[metric].label}
                          </button>
                        ))}
                      </div>
                      <select
                        className={styles.limitSelect}
                        value={provinceLimit}
                        onChange={(e) => setProvinceLimit(parseInt(e.target.value, 10))}
                        aria-label="Numero di province nel grafico"
                      >
                        <option value={5}>Top 5</option>
                        <option value={10}>Top 10</option>
                        <option value={20}>Top 20</option>
                        <option value={999}>Tutte</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.chartContainer} style={{ height: `${provinceChartHeight}px` }}>
                    <Bar
                      data={provinceBarData}
                      options={provinceBarOptions}
                      plugins={[provinceValueLabelPlugin]}
                    />
                  </div>
                  <p className={styles.chartFootnote}>
                    {PROVINCE_METRICS[provinceMetric].axisLabel} · {provinceChartRows.length} di{' '}
                    {provinceRows.length} province
                    {provinceRegione ? ` in ${provinceRegione}` : ''} · clicca una barra per vedere i
                    clienti
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Report Clienti: chi ha comprato che cosa, con filtri incrociati */}
          <div id="report-clienti">
            <CustomerReportSection
              dateRange={getDateRange(period, customStartDate, customEndDate)}
              articoli={articoli}
              province={provinceStats.map((p) => p.provincia).filter(Boolean)}
              articoloId={customerArticoloId}
              onArticoloIdChange={setCustomerArticoloId}
              provincia={customerProvincia}
              onProvinciaChange={setCustomerProvincia}
              onDataLoaded={setCustomerReport}
            />
          </div>

          {/* Charts Grid */}
          <div className={styles.chartsGrid}>
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Andamento Mensile</h3>
              <div className={styles.chartContainer}>
                <Line data={lineChartData} options={chartOptions} />
              </div>
            </div>

            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Confronto Entrate/Uscite</h3>
              <div className={styles.chartContainer}>
                <Bar data={barChartData} options={chartOptions} />
              </div>
            </div>

            <div className={styles.categoryChartsRow}>
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Entrate per Reparto</h3>
                <div className={styles.chartContainerDoughnut}>
                  {doughnutEntrateData.labels.length > 0 ? (
                    <Doughnut data={doughnutEntrateData} options={doughnutOptions} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                      Nessuna entrata nel periodo
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Uscite per Reparto</h3>
                <div className={styles.chartContainerDoughnut}>
                  {doughnutUsciteData.labels.length > 0 ? (
                    <Doughnut data={doughnutUsciteData} options={doughnutOptions} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                      Nessuna uscita nel periodo
                    </div>
                  )}
                </div>
              </div>
            </div>

            {articleCategoryRows.length > 0 && (
              <div className={`${styles.chartCardFull} ${styles.breakdownCard}`}>
                <h3 className={styles.chartTitle}>Movimenti per Merceologia</h3>
                <div className={styles.breakdownLayout}>
                  <div className={styles.chartContainerDoughnut}>
                    <Doughnut data={doughnutArticleCategoryData} options={doughnutOptionsNoLegend} />
                  </div>
                  <div className={styles.breakdownTableWrapper}>
                    <table className={styles.breakdownTable}>
                      <thead>
                        <tr>
                          <th>Merceologia</th>
                          <th style={{ textAlign: 'right' }} className={styles.hideOnMobile}>Mov.</th>
                          <th style={{ textAlign: 'right' }}>Totale</th>
                          <th style={{ textAlign: 'right' }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articleCategoryRows.map((cat) => (
                          <tr key={cat.categoriaArticoloId}>
                            <td>
                              <span className={styles.colorDot} style={{ backgroundColor: cat.colore }} />
                              {cat.categoriaArticoloNome}
                            </td>
                            <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>{cat.numeroMovimenti}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(cat.totale)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                              {cat.percentualeDelTotale.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Totale</td>
                          <td style={{ textAlign: 'right' }} className={styles.hideOnMobile}>
                            {articleCategoryTotals.numeroMovimenti}
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(articleCategoryTotals.totale)}</td>
                          <td style={{ textAlign: 'right' }}>100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Type Stats Table - Separate Entrate/Uscite */}
            {paymentTypeStats.length > 0 && (
              <div className={styles.chartCardFull}>
                <h3 className={styles.chartTitle}>
                  <Coins size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Totali per Tipo Pagamento
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: '24px', marginTop: '16px' }}>
                  {/* Entrate Table */}
                  <div>
                    <h4 style={{ marginBottom: '12px', color: 'var(--success-main)', fontSize: '1rem' }}>
                      <TrendingUp size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Entrate
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600 }}>Tipo Pagamento</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Mov.</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Totale</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentTypeStats.filter(pt => pt.tipo === 'ENTRATA').map((pt) => (
                          <tr key={`${pt.tipoPagamento}-${pt.tipo}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '10px 8px' }}>{pt.tipoPagamento}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>{pt.numeroMovimenti}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--success-main)' }}>
                              {formatCurrency(pt.totale)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                              {pt.percentualeDelTotale.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                        {paymentTypeStats.filter(pt => pt.tipo === 'ENTRATA').length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                              Nessuna entrata registrata
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Uscite Table */}
                  <div>
                    <h4 style={{ marginBottom: '12px', color: 'var(--error-main)', fontSize: '1rem' }}>
                      <TrendingDown size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Uscite
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600 }}>Tipo Pagamento</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Mov.</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Totale</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentTypeStats.filter(pt => pt.tipo === 'USCITA').map((pt) => (
                          <tr key={`${pt.tipoPagamento}-${pt.tipo}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '10px 8px' }}>{pt.tipoPagamento}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>{pt.numeroMovimenti}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--error-main)' }}>
                              {formatCurrency(pt.totale)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                              {pt.percentualeDelTotale.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                        {paymentTypeStats.filter(pt => pt.tipo === 'USCITA').length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                              Nessuna uscita registrata
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Category Totals Table */}
            {categoryStats.length > 0 && (
              <div className={styles.chartCardFull}>
                <h3 className={styles.chartTitle}>Totali per Reparto</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: '24px', marginTop: '16px' }}>
                  {/* Entrate Table */}
                  <div>
                    <h4 style={{ marginBottom: '12px', color: 'var(--success-main)', fontSize: '1rem' }}>
                      <TrendingUp size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Entrate
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600 }}>Reparto</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Mov.</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Totale</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryStats.filter(c => c.tipo === 'ENTRATA').map((cat) => (
                          <React.Fragment key={cat.categoriaId}>
                            <tr style={{ borderBottom: cat.sottocategorie?.length ? 'none' : '1px solid var(--border-color)', backgroundColor: cat.sottocategorie?.length ? 'var(--background-default)' : undefined }}>
                              <td style={{ padding: '10px 8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: cat.sottocategorie?.length ? 600 : 400 }}>
                                {cat.colore && (
                                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: cat.colore, flexShrink: 0 }} />
                                )}
                                {cat.categoriaNome || cat.nome}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: cat.sottocategorie?.length ? 600 : 400 }}>{cat.numeroMovimenti}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--success-main)' }}>
                                {formatCurrency(cat.totale)}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                                {(cat.percentualeDelTotale || cat.percentuale || 0).toFixed(1)}%
                              </td>
                            </tr>
                            {cat.sottocategorie?.map((sub) => (
                              <tr key={sub.categoriaId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '6px 8px 6px 32px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                  {sub.colore ? (
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sub.colore, flexShrink: 0 }} />
                                  ) : (
                                    <span style={{ width: '8px', flexShrink: 0 }} />
                                  )}
                                  {sub.categoriaNome}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--text-secondary)' }}>{sub.numeroMovimenti}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--success-main)' }}>
                                  {formatCurrency(sub.totale)}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                  {(sub.percentualeDelTotale || 0).toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                        {categoryStats.filter(c => c.tipo === 'ENTRATA').length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                              Nessuna entrata registrata
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Uscite Table */}
                  <div>
                    <h4 style={{ marginBottom: '12px', color: 'var(--error-main)', fontSize: '1rem' }}>
                      <TrendingDown size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Uscite
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ textAlign: 'left', padding: '10px 8px', fontWeight: 600 }}>Reparto</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Mov.</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>Totale</th>
                          <th style={{ textAlign: 'right', padding: '10px 8px', fontWeight: 600 }}>%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryStats.filter(c => c.tipo === 'USCITA').map((cat) => (
                          <React.Fragment key={cat.categoriaId}>
                            <tr style={{ borderBottom: cat.sottocategorie?.length ? 'none' : '1px solid var(--border-color)', backgroundColor: cat.sottocategorie?.length ? 'var(--background-default)' : undefined }}>
                              <td style={{ padding: '10px 8px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: cat.sottocategorie?.length ? 600 : 400 }}>
                                {cat.colore && (
                                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: cat.colore, flexShrink: 0 }} />
                                )}
                                {cat.categoriaNome || cat.nome}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: cat.sottocategorie?.length ? 600 : 400 }}>{cat.numeroMovimenti}</td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 500, color: 'var(--error-main)' }}>
                                {formatCurrency(cat.totale)}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                                {(cat.percentualeDelTotale || cat.percentuale || 0).toFixed(1)}%
                              </td>
                            </tr>
                            {cat.sottocategorie?.map((sub) => (
                              <tr key={sub.categoriaId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td style={{ padding: '6px 8px 6px 32px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                  {sub.colore ? (
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: sub.colore, flexShrink: 0 }} />
                                  ) : (
                                    <span style={{ width: '8px', flexShrink: 0 }} />
                                  )}
                                  {sub.categoriaNome}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--text-secondary)' }}>{sub.numeroMovimenti}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--error-main)' }}>
                                  {formatCurrency(sub.totale)}
                                </td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                  {(sub.percentualeDelTotale || 0).toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                        {categoryStats.filter(c => c.tipo === 'USCITA').length === 0 && (
                          <tr>
                            <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                              Nessuna uscita registrata
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          </div>
        </>
      )}
    </div>
  );
};

export default ReportPage;
